import { isEmpty } from 'lodash';
import Constants from '../../Constants';
import * as actionTypes from '../../constants/actionTypes';
import { DELETE, GET, POST, PUT } from '../../constants/requestMethods';
import {
  ACLQueryModeTypes,
  RESOURCE_SUGGESTIONS_COUNT,
  SearchFilterViews,
} from '../../constants/visibilityFilters';
import { processSearchFilters } from '../../lib/SearchFilterUtils';
import { isStateStale } from '../../lib/StateUtils';
import { CALL_PRIVATE_API } from '../../middleware/api';
import { docSchema, transformSchema } from '../../schemas';
import { setTotalItems, setTotalPages } from '../app';

// TODO: remove this unused action and related code.
export const getReusableRecordTransforms = () => (dispatch, getState) => {
  return dispatch({
    [CALL_PRIVATE_API]: {
      types: [
        actionTypes.GET_REUSABLE_RECORD_TRANSFORMS_REQUEST,
        actionTypes.GET_REUSABLE_RECORD_TRANSFORMS_SUCCESS,
        actionTypes.GET_REUSABLE_RECORD_TRANSFORMS_FAILURE,
      ],
      endpoint: `/transforms`,
      payload: {
        reusable: 1,
      },
      schema: [transformSchema],
      requestMethod: GET,
      aclQueryMode,
    },
  }).then((response) => {
    if (isStateStale(getState().transforms.reusableRecordTransforms.lastUpdatedPublic)) {
      return dispatch({
        [CALL_PRIVATE_API]: {
          types: [
            actionTypes.GET_PUBLIC_REUSABLE_RECORD_TRANSFORMS_REQUEST,
            actionTypes.GET_PUBLIC_REUSABLE_RECORD_TRANSFORMS_SUCCESS,
            actionTypes.GET_PUBLIC_REUSABLE_RECORD_TRANSFORMS_FAILURE,
          ],
          endpoint: `/transforms/public`,
          payload: {
            reusable: 1,
          },
          schema: [transformSchema],
          requestMethod: GET,
          aclQueryMode: ACLQueryModeTypes.MODE_OWNER.key,
        },
      });
    }
  });
};

export const getCodeContainer = (id) => (dispatch) => {
  return dispatch({
    [CALL_PRIVATE_API]: {
      types: [
        actionTypes.GET_CODE_CONTAINER_REQUEST,
        actionTypes.GET_CODE_CONTAINER_SUCCESS,
        actionTypes.GET_CODE_CONTAINER_FAILURE,
      ],
      endpoint: `/code_containers/${id}`,
      schema: transformSchema,
      requestMethod: GET,
      aclQueryMode: ACLQueryModeTypes.MODE_COLLABORATOR.key,
    },
  });
};

export const updateCodeContainer = (id, payload) => (dispatch) => {
  return dispatch({
    [CALL_PRIVATE_API]: {
      types: [
        actionTypes.UPDATE_CODE_CONTAINER_REQUEST,
        actionTypes.UPDATE_CODE_CONTAINER_SUCCESS,
        actionTypes.UPDATE_CODE_CONTAINER_FAILURE,
      ],
      endpoint: `/code_containers/${id}`,
      payload,
      schema: transformSchema,
      requestMethod: PUT,
      requestInfo: {
        id,
      },
    },
  });
};

export const createCodeContainer = (payload) => (dispatch) => {
  return dispatch({
    [CALL_PRIVATE_API]: {
      types: [
        actionTypes.CREATE_CODE_CONTAINER_REQUEST,
        actionTypes.CREATE_CODE_CONTAINER_SUCCESS,
        actionTypes.CREATE_CODE_CONTAINER_FAILURE,
      ],
      endpoint: `/code_containers`,
      payload,
      schema: transformSchema,
      requestMethod: POST,
    },
  });
};

export const deleteCodeContainer = (id) => (dispatch) => {
  return dispatch({
    [CALL_PRIVATE_API]: {
      types: [
        actionTypes.DELETE_CODE_CONTAINER_REQUEST,
        actionTypes.DELETE_CODE_CONTAINER_SUCCESS,
        actionTypes.DELETE_CODE_CONTAINER_FAILURE,
      ],
      endpoint: `/code_containers/${id}`,
      schema: null,
      requestMethod: DELETE,
      requestInfo: {
        id,
      },
    },
  });
};

export const deleteRecordTransform = (id) => (dispatch) => {
  return dispatch({
    [CALL_PRIVATE_API]: {
      types: [
        actionTypes.DELETE_RECORD_TRANSFORM_REQUEST,
        actionTypes.DELETE_RECORD_TRANSFORM_SUCCESS,
        actionTypes.DELETE_RECORD_TRANSFORM_FAILURE,
      ],
      endpoint: `/transforms/${id}`,
      schema: null,
      requestMethod: DELETE,
      requestInfo: {
        id,
      },
    },
  });
};

export const changeRecordTransformReusability =
  (id = null, reusable = false, otherInfo = {}) =>
  (dispatch) => {
    const payload = { ...otherInfo, reusable };
    return dispatch({
      [CALL_PRIVATE_API]: {
        types: [
          actionTypes.UPDATE_RECORD_TX_REUSABILITY_REQUEST,
          actionTypes.UPDATE_RECORD_TX_REUSABILITY_SUCCESS,
          actionTypes.UPDATE_RECORD_TX_REUSABILITY_FAILURE,
        ],
        endpoint: `/transforms/${id}`,
        payload,
        schema: transformSchema,
        requestMethod: PUT,
        requestInfo: {
          id,
          reusable,
        },
      },
    });
  };

export const getAttributeTransforms =
  (aclQueryMode = ACLQueryModeTypes.MODE_OWNER.key) =>
  (dispatch, getState) => {
    return dispatch({
      [CALL_PRIVATE_API]: {
        types: [
          actionTypes.GET_ATTR_TRANSFORMS_REQUEST,
          actionTypes.GET_ATTR_TRANSFORMS_SUCCESS,
          actionTypes.GET_ATTR_TRANSFORMS_FAILURE,
        ],
        endpoint: `/attribute_transforms`,
        schema: [transformSchema],
        requestMethod: GET,
        aclQueryMode,
      },
    }).then((response) => {
      if (isStateStale(getState().transforms.attributeTransforms.lastUpdatedPublic)) {
        return dispatch({
          [CALL_PRIVATE_API]: {
            types: [
              actionTypes.GET_PUBLIC_ATTR_TRANSFORMS_REQUEST,
              actionTypes.GET_PUBLIC_ATTR_TRANSFORMS_SUCCESS,
              actionTypes.GET_PUBLIC_ATTR_TRANSFORMS_FAILURE,
            ],
            endpoint: `/attribute_transforms/public`,
            schema: [transformSchema],
            requestMethod: GET,
            aclQueryMode: ACLQueryModeTypes.MODE_OWNER.key,
          },
        });
      }
    });
  };

export const createAttributeTransform =
  (payload = {}) =>
  (dispatch) => {
    return dispatch({
      [CALL_PRIVATE_API]: {
        types: [
          actionTypes.CREATE_ATTR_TRANSFORM_REQUEST,
          actionTypes.CREATE_ATTR_TRANSFORM_SUCCESS,
          actionTypes.CREATE_ATTR_TRANSFORM_FAILURE,
        ],
        endpoint: `/attribute_transforms`,
        payload,
        schema: transformSchema,
        requestMethod: POST,
      },
    });
  };

export const updateAttributeTransform =
  (id, payload = {}) =>
  (dispatch) => {
    return dispatch({
      [CALL_PRIVATE_API]: {
        types: [
          actionTypes.UPDATE_ATTR_TRANSFORM_REQUEST,
          actionTypes.UPDATE_ATTR_TRANSFORM_SUCCESS,
          actionTypes.UPDATE_ATTR_TRANSFORM_FAILURE,
        ],
        endpoint: `/attribute_transforms/${id}`,
        payload,
        schema: transformSchema,
        requestMethod: PUT,
        requestInfo: {
          id,
        },
      },
    });
  };

export const deleteAttributeTransform = (id) => (dispatch) => {
  return dispatch({
    [CALL_PRIVATE_API]: {
      types: [
        actionTypes.DELETE_ATTR_TRANSFORM_REQUEST,
        actionTypes.DELETE_ATTR_TRANSFORM_SUCCESS,
        actionTypes.DELETE_ATTR_TRANSFORM_FAILURE,
      ],
      endpoint: `/attribute_transforms/${id}`,
      schema: null,
      requestMethod: DELETE,
      requestInfo: {
        id,
      },
    },
  });
};

// TODO: remove this unused action and related code.
export const getTransformFeatures = () => (dispatch) => {
  return dispatch({
    [CALL_PRIVATE_API]: {
      types: [
        actionTypes.GET_TRANSFORM_FEATURES_REQUEST,
        actionTypes.GET_TRANSFORM_FEATURES_SUCCESS,
        actionTypes.GET_TRANSFORM_FEATURES_FAILURE,
      ],
      endpoint: `/transform/features`,
      schema: null,
      requestMethod: GET,
      aclQueryMode: ACLQueryModeTypes.MODE_COLLABORATOR.key,
    },
  });
};

export const getAllReusableTransforms =
  (aclQueryMode = ACLQueryModeTypes.MODE_OWNER.key, payload = {}) =>
  (dispatch, getState) => {
    return dispatch({
      [CALL_PRIVATE_API]: {
        types: [
          actionTypes.GET_CODE_CONTAINERS_REQUEST,
          actionTypes.GET_CODE_CONTAINERS_SUCCESS,
          actionTypes.GET_CODE_CONTAINERS_FAILURE,
        ],
        endpoint: `/code_containers`,
        schema: [transformSchema],
        requestMethod: GET,
        payload: {
          reusable: 1,
          ...payload,
        },
        aclQueryMode,
      },
    }).then((response) => {
      if (isStateStale(getState().transforms.reusableRecordTransforms.lastUpdatedPublic)) {
        return dispatch({
          [CALL_PRIVATE_API]: {
            types: [
              actionTypes.GET_PUBLIC_CODE_CONTAINERS_REQUEST,
              actionTypes.GET_PUBLIC_CODE_CONTAINERS_SUCCESS,
              actionTypes.GET_PUBLIC_CODE_CONTAINERS_FAILURE,
            ],
            endpoint: `/code_containers/public`,
            schema: [transformSchema],
            requestMethod: GET,
            payload: {
              reusable: 1,
              ...payload,
            },
            aclQueryMode: ACLQueryModeTypes.MODE_OWNER.key,
          },
        });
      }
    });
  };

export const getReusableTransformListing =
  (page, sortCriteria, availableFilters, setGlobalPagination = true, options = {}) =>
  (dispatch, getState) => {
    const {
      app: {
        accessControl,
        visibilityFilters: { filters },
      },
      transforms: { listingTransformType },
    } = getState();

    const aclQueryMode = options?.aclQueryMode ? options.aclQueryMode : accessControl.mode;

    const { sortKey, sortOrder } = Constants.SortCriteriaInfo[sortCriteria];
    const { processedFilters } = processSearchFilters(
      [
        ...filters[SearchFilterViews.REUSABLE_TRANSFORMS],
        {
          token: 'output_type',
          value: listingTransformType,
        },
      ],
      availableFilters,
    );

    return dispatch({
      [CALL_PRIVATE_API]: {
        types: [
          actionTypes.GET_CODE_CONTAINER_LISTING_REQUEST,
          actionTypes.GET_CODE_CONTAINER_LISTING_SUCCESS,
          actionTypes.GET_CODE_CONTAINER_LISTING_FAILURE,
        ],
        endpoint: `/code_containers/search?reusable=1&page=${page}&per_page=${Constants.LISTING_ITEMS_PER_PAGE}&sort_by=${sortKey}&sort_order=${sortOrder}`,
        payload: { filters: processedFilters },
        schema: [transformSchema],
        requestMethod: POST,
        aclQueryMode,
        requestInfo: { view: SearchFilterViews.REUSABLE_TRANSFORMS },
      },
    }).then((response) => {
      if (setGlobalPagination) {
        dispatch(setTotalPages(response.totalPages));
        dispatch(setTotalItems(response.totalItems));
      }

      return response;
    });
  };

export const getAllReusableTransformsSearchSuggestions =
  (filters, availableFilters) => (dispatch, getState) => {
    const {
      app: { accessControl },
      transforms: { listingTransformType },
    } = getState();

    const aclQueryMode = accessControl.mode;

    const { sortKey, sortOrder } =
      Constants.SortCriteriaInfo[Constants.SortCriteria.LAST_CREATED_DESC];
    const { processedFilters } = processSearchFilters(
      [
        ...filters,
        {
          token: 'output_type',
          value: listingTransformType,
        },
      ],
      availableFilters,
    );

    if (isEmpty(processedFilters)) {
      return;
    }

    const params = new URLSearchParams({
      reusable: 1,
      page: 1,
      per_page: RESOURCE_SUGGESTIONS_COUNT,
      sort_by: sortKey,
      sort_order: sortOrder,
    });

    return dispatch({
      [CALL_PRIVATE_API]: {
        types: [
          actionTypes.SEARCH_CODE_CONTAINERS_REQUEST,
          actionTypes.SEARCH_CODE_CONTAINERS_SUCCESS,
          actionTypes.SEARCH_CODE_CONTAINERS_FAILURE,
        ],
        endpoint: `/code_containers/search?${params}`,
        schema: [transformSchema],
        requestMethod: POST,
        aclQueryMode,
        payload: { filters: processedFilters },
        requestInfo: { view: SearchFilterViews.REUSABLE_TRANSFORMS },
      },
    });
  };

export const getAllReusableTransformAiFunction = () => (dispatch, getState) => {
  return dispatch({
    [CALL_PRIVATE_API]: {
      types: [
        actionTypes.GET_CODE_CONTAINERS_AI_FUNCTIONS_REQUEST,
        actionTypes.GET_CODE_CONTAINERS_AI_FUNCTIONS_SUCCESS,
        actionTypes.GET_CODE_CONTAINERS_AI_FUNCTIONS_FAILURE,
      ],
      endpoint: '/code_containers/public',
      requestMethod: GET,
      payload: {
        resource_type: 'ai_function',
        ai_function_type: 'chunker',
      },
      aclQueryMode: ACLQueryModeTypes.MODE_OWNER.key,
    },
  });
};

export const fetchTransformDoc = (id) => (dispatch) => {
  return dispatch({
    [CALL_PRIVATE_API]: {
      types: [
        actionTypes.GET_TRANSFORM_DOC_REQUEST,
        actionTypes.GET_TRANSFORM_DOC_SUCCESS,
        actionTypes.GET_TRANSFORM_DOC_FAILURE,
      ],
      endpoint: `/code_containers/${id}/docs`,
      schema: [docSchema],
      requestMethod: GET,
      payload: {
        expand: 1,
      },
      requestInfo: {
        id,
      },
    },
  });
};

export const updateTransformDoc = (id, content) => (dispatch) => {
  return dispatch({
    [CALL_PRIVATE_API]: {
      types: [
        actionTypes.UPDATE_TRANSFORM_DOC_REQUEST,
        actionTypes.UPDATE_TRANSFORM_DOC_SUCCESS,
        actionTypes.UPDATE_TRANSFORM_DOC_FAILURE,
      ],
      endpoint: `/code_containers/${id}/docs?expand=1`,
      schema: [docSchema],
      requestMethod: POST,
      payload: {
        docs: [
          {
            name: `Doc for transform ${id}`,
            description: `Doc for transform ${id}`,
            doc_type: 'md',
            text: content,
          },
        ],
      },
      requestInfo: {
        id,
      },
    },
  });
};

export const setListingTransformType = (transformType) => {
  return {
    type: actionTypes.SET_LISTING_TRANSFORM_TYPE,
    transformType,
  };
};
